'use client'

import { PROVIDER_ROUTES, ROUTES } from '@/core/constants/routes'
import { useIsClient } from '@/core/hooks/use-is-client'
import { useMutation } from '@/core/hooks/use-mutation'
import { dateService } from '@/core/services/date'
import { useAuth } from '@/core/store/auth'
import { getCreateTripValidation } from '@/features/reservation/form-validation/create-trip.validation'
import { useReservationStore } from '@/features/reservation/store'
import { WaitingTime } from '@/features/reservation/types/enums/waiting-time.enum'
import { useRouter } from '@/i18n/navigation'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { addTrip, updateTrip } from '../../providers/client'
import { useOrderSummaryStore } from '../../store'
import { OrderStep } from '../../types/enums/order-step.enum'

type Props = {
  isUpdate?: boolean
  isProvider?: boolean
}
export const useCreateTrip = ({
  isUpdate = false,
  isProvider = false,
}: Props) => {
  const t = useTranslations()
  const isClient = useIsClient()
  const router = useRouter()
  const { user, isAuthenticated } = useAuth()
  const bookForm = useReservationStore(state => state.bookForm)
  const setCreatedTrip = useReservationStore(state => state.setCreatedTrip)
  const createTrip = useReservationStore(state => state.createTrip)
  const setCreateTrip = useReservationStore(state => state.setCreateTrip)
  const updateCreateTrip = useReservationStore(state => state.updateCreateTrip)
  const tripInfo = useReservationStore(state => state.tripInfo)
  const tripToUpdate = useReservationStore(state => state.tripToUpdate)
  const setOrderSummary = useOrderSummaryStore(state => state.setMany)

  const { time, fromPointTypes } = bookForm
  const isAirport = Boolean(fromPointTypes?.includes('airport'))
  const { schema: validationSchema } = getCreateTripValidation(t, {
    isAirport,
    isUpdate,
  })

  const { mutate, error, isPending } = useMutation({
    mutation:
      isUpdate && tripToUpdate?.id
        ? updateTrip.bind(null, tripToUpdate?.id.toString())
        : addTrip,
    onSuccess(result) {
      setCreatedTrip(result)
      let route = isProvider
        ? PROVIDER_ROUTES.reservationPay(result.id)
        : ROUTES.payTrip(result.id)
      if (isUpdate) {
        route = isProvider
          ? PROVIDER_ROUTES.updateTripPay(result.id)
          : ROUTES.updateTripPay(result.id)
      }
      setOrderSummary({
        price: result.price,
        totalPrice: result.totalPrice,
        discount: result.discount,
        currency: result.currency,
        customerPrice: result.customerPrice,
        providerPrice: result.providerPrice,
        step: OrderStep.PAYEMNT,
      })
      router.push(route)
    },
  })

  const getPickupTime = useCallback(() => {
    if (createTrip?.pickupTime) return createTrip?.pickupTime

    if (tripToUpdate?.pickupTime) return tripToUpdate?.pickupTime
    if (isAirport) {
      return dateService.timeToHI(
        dateService.addHours(time instanceof Date ? time : new Date(time), 1),
      )
    }
    return time instanceof Date ? dateService.timeToHI(time) : time
  }, [createTrip?.pickupTime, isAirport, time, tripToUpdate?.pickupTime])

  const form = useForm({
    defaultValues: {
      vehicleSizeId:
        createTrip?.vehicleSizeId?.toString() ||
        tripToUpdate?.vehicleSize ||
        tripInfo?.listVehicle
          .find(
            v =>
              v.key.replaceAll('_', ' ').toLowerCase() ===
              tripToUpdate?.tripType.toLowerCase(),
          )
          ?.id.toString() ||
        tripInfo?.vehicleSize.id.toString(),

      pickupTime: getPickupTime(),
      tripFor: createTrip?.tripFor || 'forMe',
      relationWithPassenger: createTrip?.relationWithPassenger || '',
      waitingTime:
        (createTrip?.waitingTime ?? isAirport)
          ? WaitingTime.AIRPORT
          : WaitingTime.DEFAULT,
      email: createTrip?.email || user?.email || '',
      phoneNumber: createTrip?.phoneNumber || user?.phoneNumber || '',
      fullName: createTrip?.fullName || user?.fullName || '',
      flightNumber:
        createTrip?.flightNumber || tripToUpdate?.flightNumber || '',
      note: createTrip?.note || tripToUpdate?.note || '',
    },
    resolver: zodResolver(validationSchema),
    disabled: !isClient,
  })

  useEffect(() => {
    const pickupTime = getPickupTime()
    form.setValue('pickupTime', pickupTime)
    updateCreateTrip({ pickupTime })
  }, [form, getPickupTime, updateCreateTrip])

  useEffect(() => {
    if (!isAuthenticated) return
    if (createTrip?.fullName && createTrip.email && createTrip.phoneNumber)
      return

    form.setValue('fullName', user?.fullName || '')
    form.setValue('email', user?.email || '')
    form.setValue('phoneNumber', user?.phoneNumber || '')
  }, [
    form,
    user?.fullName,
    user?.email,
    user?.phoneNumber,
    isAuthenticated,
    createTrip,
  ])

  useEffect(() => {
    if (
      !tripInfo ||
      !bookForm ||
      !Object.values(bookForm).every(v => v !== null && v !== undefined)
    ) {
      router.replace(ROUTES.home)
    }
  }, [bookForm, router, tripInfo])

  const handleVehicleSizeChange = useCallback(
    (id: string) => {
      const vehicle = tripInfo?.listVehicle.find(
        v => v.id.toString() === id.toString(),
      )
      if (!vehicle) return
      setOrderSummary({
        vehicleSizeName: vehicle.name,
        price: vehicle.price,
        totalPrice: vehicle.totalPrice,
      })
    },
    [setOrderSummary, tripInfo?.listVehicle],
  )

  useEffect(() => {
    if (tripInfo?.vehicleSize.id) {
      form.setValue('vehicleSizeId', tripInfo?.vehicleSize.id.toString())
      handleVehicleSizeChange(tripInfo?.vehicleSize.id.toString())
    }
  }, [handleVehicleSizeChange, tripInfo?.vehicleSize.id, form])

  const onSubmit = form.handleSubmit(values => {
    setOrderSummary({
      fullName: values.fullName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      vehicleSizeName:
        tripInfo?.listVehicle.find(
          v => v.id.toString() === values.vehicleSizeId.toString(),
        )?.name || tripInfo?.vehicleSize.name,
    })

    const sentData = {
      ...values,
      pickupTime: dateService.isValid(new Date(values.pickupTime))
        ? dateService.timeToHI(new Date(values.pickupTime))
        : values.pickupTime,
    }
    setCreateTrip(sentData)
    mutate({ ...bookForm, ...sentData })
  })
  return {
    form,
    onSubmit,
    isPending,
    isAirport,
    tripInfo,
    time,
    handleVehicleSizeChange,
    error,
  }
}
